"use client";

import React from "react";
import { UploadSimpleIcon } from "@phosphor-icons/react";
import type { PresetOption } from "@/types/createAgent";
import { cn } from "@/lib/utils";
import {
  SPRITE_LABELS,
  getPresetDescriptor,
  getSpriteStyle,
  type SpriteRef,
} from "./sprites";

function ColorSwatch({ colors }: { colors: string[] }) {
  return (
    <span
      aria-hidden="true"
      className="block size-full rounded-md shadow-inner ring-1 ring-black/10"
      style={{ background: getColorBackground(colors) }}
    />
  );
}

function getColorBackground(colors: string[]): string | undefined {
  if (colors.length <= 1) return colors[0];

  return `linear-gradient(135deg, ${colors
    .map((color, index) => {
      const start = Math.round((index / colors.length) * 100);
      const end = Math.round(((index + 1) / colors.length) * 100);
      return `${color} ${start}% ${end}%`;
    })
    .join(", ")})`;
}

function SpriteCell({
  sprite,
  className,
}: {
  sprite: SpriteRef;
  className?: string;
}) {
  return (
    <span
      data-sprite-cell={`${sprite.sheet}:${sprite.index}`}
      className={cn(
        // hue-rotate shifts the sprites' baked-in navy toward the brand
        // violet so the tiles read as part of the theme; the white garments
        // carry no saturation and are unaffected. Cheaper than regenerating
        // the bitmap sheets (Asset Rules, CreateChatActionStyle.md).
        "block size-full bg-[#fbfaf8] bg-no-repeat shadow-inner ring-1 ring-black/10 [filter:hue-rotate(40deg)]",
        className
      )}
      style={getSpriteStyle(sprite)}
    />
  );
}

function CompositeSpriteVisual({
  sprites,
  colors,
}: {
  sprites: SpriteRef[];
  colors: string[];
}) {
  const visibleSprites = sprites.slice(0, 5);

  return (
    <span
      data-composite-icon-grid
      className="grid aspect-[3/2] w-full grid-cols-3 grid-rows-2 gap-0.5 rounded-md bg-muted/60 p-0.5"
    >
      {colors.length > 0 && (
        <span
          data-composite-icon-cell="color"
          className="aspect-square min-h-0 min-w-0 overflow-hidden"
        >
          <ColorSwatch colors={colors} />
        </span>
      )}
      {visibleSprites.map(sprite => (
        <span
          key={`${sprite.sheet}-${sprite.index}`}
          data-composite-icon-cell={sprite.sheet}
          className="aspect-square min-h-0 min-w-0 overflow-hidden"
        >
          <SpriteCell sprite={sprite} className="rounded-[4px]" />
        </span>
      ))}
    </span>
  );
}

function VisualPreviewTile({
  label,
  dataKey,
  children,
  compactOnMobile = false,
}: {
  label: string;
  dataKey?: string;
  children: React.ReactNode;
  compactOnMobile?: boolean;
}) {
  return (
    <span
      data-vibe-attribute-cell={(dataKey ?? label).toLowerCase()}
      className={cn(
        "flex min-w-0 flex-col items-center gap-1",
        compactOnMobile && "max-sm:gap-0"
      )}
    >
      <span
        data-vibe-attribute-icon
        className={cn(
          "block aspect-square w-full max-w-16 overflow-hidden rounded-lg bg-muted/40 shadow-inner ring-1 ring-border/70",
          compactOnMobile && "max-sm:max-w-14"
        )}
      >
        {children}
      </span>
      <span
        data-vibe-attribute-label
        className={cn(
          "max-w-full text-center text-[11px] font-semibold leading-tight text-foreground/70",
          compactOnMobile ? "hidden sm:line-clamp-2" : "line-clamp-2"
        )}
      >
        {label}
      </span>
    </span>
  );
}

function CompositePreviewGrid({
  sprites,
  colors,
  colorLabel,
  compactOnMobile = false,
}: {
  sprites: SpriteRef[];
  colors: string[];
  colorLabel?: string;
  compactOnMobile?: boolean;
}) {
  // Direction cards are deliberately a fast three-part comparison. Showing
  // every structured field made the labels wrap into individual letters at
  // the same breakpoint where three cards fit side-by-side.
  const comparisonSprites = ["silhouettes", "fabrics"].flatMap(sheet => {
    const sprite = sprites.find(candidate => candidate.sheet === sheet);
    return sprite ? [sprite] : [];
  });

  return (
    <span
      data-vibe-attribute-grid
      className={cn(
        "relative grid min-w-0 gap-1.5 overflow-hidden rounded-lg bg-muted/30 p-1.5",
        compactOnMobile && "max-sm:gap-1 max-sm:p-1",
        colors.length > 0 ? "grid-cols-3" : "grid-cols-2"
      )}
    >
      {colors.length > 0 && (
        <span
          data-vibe-color-wash
          aria-hidden="true"
          className="absolute inset-0 opacity-20"
          style={{ background: getColorBackground(colors) }}
        />
      )}
      {colors.length > 0 && (
        <VisualPreviewTile
          label="Color"
          dataKey="color"
          compactOnMobile={compactOnMobile}
        >
          <span
            data-vibe-color-label={colorLabel ?? "Color"}
            className="block size-full"
          >
            <ColorSwatch colors={colors} />
          </span>
        </VisualPreviewTile>
      )}
      {comparisonSprites.map(sprite => (
        <VisualPreviewTile
          key={`${sprite.sheet}-${sprite.index}`}
          label={SPRITE_LABELS[sprite.sheet]}
          compactOnMobile={compactOnMobile}
        >
          <SpriteCell sprite={sprite} className="rounded-md" />
        </VisualPreviewTile>
      ))}
    </span>
  );
}

export function hasActionOptionVisual(preset: PresetOption): boolean {
  return getPresetDescriptor(preset) !== null;
}

export function hasCompleteVibeComparison(preset: PresetOption): boolean {
  const descriptor = getPresetDescriptor(preset);
  if (descriptor?.kind !== "composite" || descriptor.colors.length === 0) {
    return false;
  }

  const spriteSheets = new Set(descriptor.sprites.map(sprite => sprite.sheet));
  return spriteSheets.has("silhouettes") && spriteSheets.has("fabrics");
}

export function ActionOptionPreviewGrid({
  preset,
  className,
  compactOnMobile = false,
}: {
  preset: PresetOption;
  className?: string;
  compactOnMobile?: boolean;
}) {
  const descriptor = getPresetDescriptor(preset);
  if (!descriptor) return null;

  return (
    <span className={cn("block min-w-0", className)} aria-hidden="true">
      {descriptor.kind === "composite" && (
        <CompositePreviewGrid
          sprites={descriptor.sprites}
          colors={descriptor.colors}
          colorLabel={descriptor.colorLabel}
          compactOnMobile={compactOnMobile}
        />
      )}
      {descriptor.kind === "color" && (
        <span data-vibe-attribute-grid className="block size-12">
          <ColorSwatch colors={descriptor.colors} />
        </span>
      )}
      {descriptor.kind === "sprite" && (
        <span data-vibe-attribute-grid className="block size-12">
          <SpriteCell sprite={descriptor.sprite} className="rounded-md" />
        </span>
      )}
    </span>
  );
}

export function ActionOptionVisual({
  preset,
  className,
}: {
  preset: PresetOption;
  className?: string;
}) {
  const descriptor = getPresetDescriptor(preset);
  if (!descriptor) return null;

  return (
    <span
      data-option-asset={descriptor.kind}
      aria-hidden="true"
      className={cn(
        "flex size-full items-center justify-center overflow-hidden rounded-md bg-background shadow-inner ring-1 ring-black/10",
        className
      )}
    >
      {descriptor.kind === "color" && (
        <ColorSwatch colors={descriptor.colors} />
      )}
      {descriptor.kind === "sprite" && (
        <SpriteCell sprite={descriptor.sprite} className="rounded-md" />
      )}
      {descriptor.kind === "composite" && (
        <CompositeSpriteVisual
          sprites={descriptor.sprites}
          colors={descriptor.colors}
        />
      )}
      {descriptor.kind === "upload" && (
        <UploadSimpleIcon
          size={17}
          weight="duotone"
          className="text-foreground/75"
        />
      )}
    </span>
  );
}
